"use client";

import React, { useState, useCallback, ButtonHTMLAttributes } from 'react';
import { useCheckout } from '../providers/UseCeloProvider';
import { CheckoutOptions } from '../types/payment';

export interface CheckoutButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'onError'> {
  planId: string;
  userId?: string;
  email?: string;
  successUrl?: string;
  cancelUrl?: string;
  metadata?: Record<string, string | number | boolean>;
  trialDays?: number;
  quantity?: number;
  couponCode?: string;

  // UI props
  loadingText?: string;
  errorText?: string;
  onSuccess?: (sessionUrl: string) => void;
  onCheckoutError?: (error: Error) => void;

  // Behavior
  openInNewTab?: boolean;
  requireConfirmation?: boolean;
  confirmationMessage?: string;
}

/**
 * CheckoutButton - A self-contained button component for initiating checkout
 * Works with any configured payment gateway
 */
export function CheckoutButton({
  planId,
  userId,
  email,
  successUrl,
  cancelUrl,
  metadata,
  trialDays,
  quantity,
  couponCode,
  loadingText = 'Processing...',
  errorText,
  onSuccess,
  onCheckoutError,
  openInNewTab = false,
  requireConfirmation = false,
  confirmationMessage = 'Proceed to checkout?',
  children = 'Checkout',
  disabled,
  className = '',
  ...buttonProps
}: CheckoutButtonProps) {
  const { initiateCheckout, isReady, gateway } = useCheckout();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCheckout = useCallback(async () => {
    // Confirmation check
    if (requireConfirmation && !window.confirm(confirmationMessage)) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const checkoutOptions: Partial<CheckoutOptions> = {
        userId,
        email,
        successUrl,
        cancelUrl,
        metadata,
        trialDays,
        quantity,
        couponCode
      };

      if (openInNewTab) {
        // For new tab, we need to get the URL first
        const { getCheckoutUrl } = require('./UseCeloProvider').useUseCelo();
        const url = await getCheckoutUrl(planId, checkoutOptions);
        window.open(url, '_blank');
        onSuccess?.(url);
      } else {
        // Normal flow - redirect in same window
        await initiateCheckout(planId, checkoutOptions);
        // Note: This line won't be reached as page redirects
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Checkout failed';
      setError(errorMessage);
      onCheckoutError?.(err instanceof Error ? err : new Error(errorMessage));
    } finally {
      setIsLoading(false);
    }
  }, [
    planId,
    userId,
    email,
    successUrl,
    cancelUrl,
    metadata,
    trialDays,
    quantity,
    couponCode,
    requireConfirmation,
    confirmationMessage,
    openInNewTab,
    initiateCheckout,
    onSuccess,
    onCheckoutError
  ]);

  // Determine button state and text
  const isDisabled = disabled || !isReady || isLoading;
  const displayError = error || errorText;

  const getButtonText = () => {
    if (isLoading) return loadingText;
    if (displayError && errorText) return errorText;
    return children;
  };

  return (
    <>
      <button
        {...buttonProps}
        className={className}
        disabled={isDisabled}
        onClick={handleCheckout}
        aria-busy={isLoading}
        aria-disabled={isDisabled}
        data-gateway={gateway}
      >
        {getButtonText()}
      </button>

      {displayError && !errorText && (
        <div
          className="checkout-error"
          style={{
            color: 'red',
            fontSize: '0.875rem',
            marginTop: '0.5rem'
          }}
        >
          {displayError}
        </div>
      )}
    </>
  );
}

/**
 * Hook for creating custom checkout buttons
 */
export function useCheckoutButton(planId: string, options?: Partial<CheckoutOptions>) {
  const { initiateCheckout, isReady, gateway } = useCheckout();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const checkout = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      await initiateCheckout(planId, options);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Checkout failed');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [planId, options, initiateCheckout]);

  return {
    checkout,
    isLoading,
    isReady,
    error,
    gateway
  };
}